<template>
  <main class="grow alt-hub">
    <!-- Same dark band and card idiom as the /alternatives hub, so the two switching hubs read as one set. -->
    <header class="alt-hub-hero">
      <div class="alt-hub-shell">
        <nav class="alt-hub-crumb" aria-label="Breadcrumb">
          <NuxtLink :to="localePath('/')">Home</NuxtLink>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Switch</span>
        </nav>
        <h1>Switching to Workspace369, tool by tool.</h1>
        <p class="alt-hub-lede">
          One moving guide for each tool people leave: what comes across with AI Import, what you set up again by
          hand, and the order to do it in. AI Import reads CSV or Excel exports of clients, services, invoices,
          proposals and expenses, up to what your plan includes, and you check every row before it saves. There is no one-click importer.
        </p>
        <div class="hub-actions">
          <a
            :href="addReferralToUrl('https://app.workspace369.com/signup')"
            data-conversion-event="start_free_click"
            data-conversion-label="Switch hub start free"
            data-conversion-location="switch-hub-hero"
          >
            Start free trial
          </a>
          <!-- Terms directly under the trial button (B1.1), as on the alternatives hub. -->
          <TrialDisclosure on-dark />
          <NuxtLink
            :to="localePath('/pricing')"
            data-conversion-event="pricing_click"
            data-conversion-label="Switch hub compare plans"
            data-conversion-location="switch-hub-hero"
          >Compare plans</NuxtLink>
        </div>
        <p class="sw-hub-note">
          Competitor prices on these cards come from each guide, which links the vendor's pricing page and says when it
          was checked.
        </p>
        <nav class="sw-hub-jump" aria-label="Jump to a group">
          <a v-for="group in switchGroups" :key="group.id" :href="`#${group.id}`">{{ group.title }}</a>
        </nav>
      </div>
    </header>

    <section v-for="group in switchGroups" :id="group.id" :key="group.id" class="alt-hub-library sw-hub-group" :aria-labelledby="`${group.id}-title`">
      <div class="alt-hub-shell">
        <h2 :id="`${group.id}-title`" class="alt-hub-popular-title">{{ group.title }}</h2>
        <ul class="alt-hub-cards">
          <li v-for="page in group.pages" :key="page.slug">
            <NuxtLink
              :to="localePath(`/switch/${page.slug}`)"
              class="alt-hub-card"
              data-conversion-event="comparison_click"
              :data-conversion-label="`Switch hub card ${page.name}`"
              data-conversion-location="switch-hub-cards"
            >
              <!-- The group heading names the kind of tool and each description starts "Switch from <name>",
                   so the card carries only the name, the guide's own summary and the link. -->
              <h3 translate="no">{{ page.name }}</h3>
              <p>{{ page.description }}</p>
              <p class="alt-hub-card-bottom">
                <strong>Read the guide →</strong>
              </p>
            </NuxtLink>
          </li>
        </ul>
      </div>
    </section>
    <Cta03 />
    <PhoneStickyCta label="Switch hub" :location="$route.path" watch=".alt-hub-hero .hub-actions" />
  </main>
</template>

<script setup>
import { switchGroups, switchPages } from '~/data/switch-hub';
import { localizedUrl } from '~/data/locales';
import { ogImagePathForRoute } from '~/utils/og-image';

const { canonicalLocaleCode, isCurrentPageIndexable, localePath } = useWorkspaceLocale();
const { addReferralToUrl } = useReferral();
const siteUrl = 'https://workspace369.com';

const canonicalUrl = localizedUrl(siteUrl, '/switch', canonicalLocaleCode.value);
// Plain strings so scripts/meta-length.test.mjs can read them; scripts/switch-hub.test.mjs fails when the count
// stops matching the number of guides.
const title = 'Switching Guides for 37 Tools | Workspace369';
const description =
  'Moving guides for 37 tools: what AI Import brings across from your CSV or Excel exports, what you set up again, and a checklist for each switch.';
const image = `${siteUrl}${ogImagePathForRoute('/switch')}`;

useSeoMeta({
  title,
  description,
  ogType: 'website',
  ogTitle: title,
  ogDescription: description,
  ogUrl: canonicalUrl,
  ogImage: image,
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: image,
});

useHead({
  meta: [
    {
      key: 'robots',
      name: 'robots',
      content: isCurrentPageIndexable.value ? 'index, follow' : 'noindex, follow',
    },
  ],
  link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl }],
  script: [
    {
      key: 'switch-hub-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          { '@type': 'CollectionPage', '@id': `${canonicalUrl}#webpage`, name: title, description, url: canonicalUrl },
          {
            '@type': 'ItemList',
            '@id': `${canonicalUrl}#guides`,
            itemListElement: switchPages.map((page, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: `Switch from ${page.name}`,
              url: `${siteUrl}/switch/${page.slug}`,
            })),
          },
          {
            '@type': 'BreadcrumbList',
            '@id': `${canonicalUrl}#breadcrumb`,
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
              { '@type': 'ListItem', position: 2, name: 'Switch', item: canonicalUrl },
            ],
          },
        ],
      }),
    },
  ],
});
</script>

<style scoped>
.alt-hub {
  background: #ffffff;
  color: #0f172a;
}

.alt-hub-shell {
  width: min(100% - 2rem, 72rem);
  margin-inline: auto;
}

/* Hero: same dark band as the detail pages */
.alt-hub-hero {
  background: #020617;
  padding: 6.5rem 0 3.5rem;
}

@media (min-width: 900px) {
  .alt-hub-hero {
    padding: 10rem 0 5rem;
  }
}

.alt-hub-crumb {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  color: #94a3b8;
  font-size: 0.78rem;
  font-weight: 700;
}

.alt-hub-crumb a {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  margin-block: -0.6rem;
  color: #94a3b8;
  text-decoration: none;
}

.alt-hub-crumb a:hover {
  color: #67e8f9;
}

.alt-hub-crumb span[aria-hidden] {
  color: #475569;
}

.alt-hub-hero h1 {
  margin-top: 1.75rem;
  max-width: 46rem;
  color: #ffffff;
  font-size: clamp(2.1rem, 5vw, 3.5rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.05;
  text-wrap: balance;
}

.alt-hub-lede {
  margin-top: 1.25rem;
  max-width: 44rem;
  color: #cbd5e1;
  font-size: clamp(1rem, 1.8vw, 1.13rem);
  line-height: 1.7;
}

.hub-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.6rem;
}

.hub-actions a {
  display: inline-flex;
  min-height: 2.9rem;
  min-width: 9.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 0.7rem 1.1rem;
  font-size: 0.92rem;
  font-weight: 800;
  text-decoration: none;
}

/* max-width none: with its default 34rem cap the line can fit beside the buttons on wide screens instead of under them. */
.hub-actions :deep(.trial-disclosure) {
  flex: 0 0 100%;
  max-width: none;
  margin: 0;
}

@media (min-width: 640px) {
  .hub-actions :deep(.trial-disclosure) {
    order: 2;
  }
}

.hub-actions a:first-child {
  background: #0e7490;
  color: #fff;
}

.hub-actions a:first-child:hover {
  background: #0b6a84;
}

.hub-actions a:last-child {
  border: 1px solid rgb(255 255 255 / 0.22);
  color: #fff;
}

/* Card library: research-card idiom — hairline, radius, hover border only */
.alt-hub-library {
  padding: 3.5rem 0 3.5rem;
}

/* Groups follow each other: only the first gets the full top space, and the jump links land below the header. */
.sw-hub-group + .sw-hub-group {
  padding-top: 0;
}

.sw-hub-group {
  /* Clears the sticky header (about 117px at desktop, with the trial line) so a jump lands on the heading. */
  scroll-margin-top: 9rem;
}

/* Three columns from 1100px: 39 short cards read faster as a grid than as a long two-column scroll. */
@media (min-width: 1100px) {
  .sw-hub-group .alt-hub-cards {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.sw-hub-group .alt-hub-card h3 {
  margin-top: 0;
}

.sw-hub-group .alt-hub-card-bottom {
  justify-content: flex-end;
}

.sw-hub-group .alt-hub-popular-title {
  font-size: 1.35rem;
}

/* Where card prices come from: small print on the dark band, under the CTAs. */
.sw-hub-note {
  max-width: 42rem;
  margin: 1.75rem 0 0;
  color: #94a3b8;
  font-size: 0.85rem;
  line-height: 1.6;
}

/* Jump links to each group: chips on the dark band, 44px tall for thumbs. */
.sw-hub-jump {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.sw-hub-jump a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 0.9rem;
  border: 1px solid rgb(255 255 255 / 0.18);
  border-radius: 6px;
  color: #e2e8f0;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
}

.sw-hub-jump a:hover,
.sw-hub-jump a:focus-visible {
  border-color: rgb(255 255 255 / 0.45);
  color: #fff;
}

.alt-hub-popular-title {
  margin: 0 0 0.75rem;
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 800;
}

.alt-hub-cards {
  display: grid;
  gap: 1.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

@media (min-width: 760px) {
  .alt-hub-cards {
    grid-template-columns: 1fr 1fr;
  }
}

.alt-hub-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 1.75rem;
  text-decoration: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.alt-hub-card:hover {
  border-color: #83c3d8;
  box-shadow: 0 8px 22px rgb(18 49 40 / 0.03);
}

.alt-hub-card h3 {
  margin: 1.1rem 0 0.7rem;
  color: #0f172a;
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.alt-hub-card > p:not(.alt-hub-card-top):not(.alt-hub-card-bottom) {
  margin: 0;
  color: #475569;
  font-size: 0.88rem;
  line-height: 1.7;
}

.alt-hub-card-bottom {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin: 1.5rem 0 0;
  border-top: 1px solid #e8eef4;
  padding-top: 1rem;
  color: #64748b;
  font-size: 0.74rem;
}

.alt-hub-card-bottom strong {
  color: #0e7490;
  font-weight: 700;
}

/* Phones: tighter cards, every word kept (same values as the /alternatives hub, #1519). */
@media (max-width: 639px) {
  .alt-hub-cards {
    gap: 0.75rem;
  }
  .alt-hub-card {
    padding: 1.1rem 1.15rem;
  }
  .alt-hub-card h3 {
    margin: 0.55rem 0 0.4rem;
    font-size: 1.15rem;
    line-height: 1.25;
  }
  .alt-hub-card > p:not(.alt-hub-card-top):not(.alt-hub-card-bottom) {
    line-height: 1.55;
  }
  .alt-hub-card-bottom {
    margin-top: 0.8rem;
    padding-top: 0.65rem;
  }
}

.alt-hub-card:focus-visible,
.alt-hub-hero a:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 3px;
}

/* Dark mode (site toggle): same palette as the compare hub and the alternatives detail pages. */
.dark .alt-hub { background: #020617; color: #f1f5f9; }
.dark .alt-hub-popular-title,
.dark .alt-hub-card h3 { color: #f1f5f9; }
.dark .alt-hub-card-bottom strong { color: #67e8f9; }
.dark .alt-hub-card { border-color: #1f2b3d; background: #0b1220; }
.dark .alt-hub-card:hover { border-color: #22b8dd; }
.dark .alt-hub-card > p:not(.alt-hub-card-top):not(.alt-hub-card-bottom) { color: #a5b4c6; }
.dark .alt-hub-card-bottom { border-color: #1f2b3d; color: #94a3b8; }

@media (prefers-reduced-motion: reduce) {
  .alt-hub-card {
    transition: none;
  }
}
</style>
